import { Flex, message } from 'antd';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import DateRange from 'components/DateRange';
import HeaderCard from 'components/HeaderCard';
import MultiSelect from 'components/MultiSelect';
import SearchBox from 'components/SearchBox';
import SwitchComponent from 'components/Switch';

import PackageDetailsDrawer from './components/drawer/PackageDetailsDrawer';
import PackageFilterLayout from './components/PackageFilterLayout';
import PackageTable from './components/table/PackageTable';
import PackageCountWidget from './components/widgets/PackageCount';
import PackageCountChart from './components/widgets/PackageCountChart';
import PackageCountWidgetLayout from './components/widgets/PackageCountWidgetLayout';
import PackageSummaryWidgetLayout from './components/widgets/PackageSummaryWidgetLayout';
import SignedInvoicesWidget from './components/widgets/SignedInvoicesWidget';
import StuckPackagesWidget from './components/widgets/StuckPackagesWidget';

import useAgronomistList from 'hooks/useAgronomistList';
import useDebounceValue from 'hooks/useDebounceValue';
import useFarmerGroup from 'screens/farmer-groups/hooks/useFarmerGroupListQuery';
import usePackagesQuery from './hooks/usePackagesQuery';
import usePackageSummaryQuery from './hooks/usePackageSummaryQuery';
import useStuckPackageCountQuery from './hooks/useStuckPackageCountQuery';

import { selectToken, selectUserCountry } from 'store/auth/selectors';

import { filterReducer, FilterType, initialFilterState } from './store/reducer';
import { constructPackageListQueryParams } from './util';

import './style.scss';
import type { PackageDTO } from './types';

import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';
import useNotifyError from 'hooks/useNotifyError';
import { ROLE_GROUPS } from 'utils/constants';
import {
  SHIPMENT_STATUS_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from './helper';

function PackageTracker() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedPackage, setSelectedPackage] = useState<PackageDTO | null>(
    null,
  );

  const [farmerGroupSearchTerm, setFarmerGroupSearchTerm] = useState('');

  const debouncedSearchTerm = useDebounceValue(farmerGroupSearchTerm, 500);

  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();

  const [filters, dispatchFilters] = useReducer(
    filterReducer,
    initialFilterState,
  );

  const country = useSelector(selectUserCountry);
  const token = useSelector(selectToken);

  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country);

  const filterParams = useMemo(() => {
    return constructPackageListQueryParams(
      page,
      pageSize,
      filters?.agronomist,
      filters?.packageStatus,
      filters?.shipmentStatus,
      filters?.soNumber,
      filters?.farmerGroup,
      filters?.isViewingStuckPackages,
      filters?.isViewingPackagesWithoutInvoice,
      filters?.dateRange,
    );
  }, [page, pageSize, filters]);

  // Data fetching hooks
  const {
    data: packageList,
    isLoading: isSalesOrderListLoading,
    error: packageListError,
    totalCount: totalPackageCount,
  } = usePackagesQuery({ params: filterParams, token });

  const packageTrackerCommonQueryParams = useMemo(() => {
    return {
      'packageCreatedDate.greaterThanOrEqual': filters.dateRange?.[0],
      'packageCreatedDate.lessThanOrEqual': filters.dateRange?.[1],
    };
  }, [filters?.dateRange]);

  const { data: packageSummary, isLoading: isPackageSummaryLoading } =
    usePackageSummaryQuery({ params: packageTrackerCommonQueryParams, token });

  const { data: count, isLoading: isStuckPackageLoading } =
    useStuckPackageCountQuery({
      params: packageTrackerCommonQueryParams,
      token,
    });

  const { farmerGroupOptions, isLoading: isFarmerGroupLoading } =
    useFarmerGroup({
      size: 20,
      search: debouncedSearchTerm,
      country: country,
    });

  const handleTogglePackagesWithoutInvoice = useCallback(() => {
    dispatchFilters({
      type: FilterType.TOGGLE_PACKAGES_WITHOUT_INVOICE,
      payload: !filters.isViewingPackagesWithoutInvoice,
    });
    messageApi.destroy();
    messageApi.info(
      filters.isViewingPackagesWithoutInvoice
        ? t('Showing all packages')
        : t('Showing only packages without invoices'),
    );
  }, [filters.isViewingPackagesWithoutInvoice, messageApi, t]);

  const handleToggleStuckPackages = useCallback(
    (isViewing: boolean) => {
      dispatchFilters({
        type: FilterType.TOGGLE_STUCK_PACKAGES,
        payload: isViewing,
      });
      messageApi.destroy();
      messageApi.info(
        isViewing
          ? t('Showing only the packages that are stuck for more than 2 days')
          : t('Showing all packages'),
      );
    },
    [messageApi, t],
  );

  const handlePageSizeChange = (currentPage: number, size: number) => {
    setPage(currentPage - 1);
    setPageSize(size);
  };

  const handlePackageRowSelection = useCallback((pkg: PackageDTO | null) => {
    setSelectedPackage(pkg);
  }, []);

  const handleSoSearchChange = useCallback((soNumber: string) => {
    dispatchFilters({
      type: FilterType.SET_SO_NUMBER,
      payload: soNumber,
    });
  }, []);

  const handlePackageStatusChange = useCallback((status: string[]) => {
    dispatchFilters({
      type: FilterType.SET_PACKAGE_STATUS,
      payload: status,
    });
  }, []);

  const handleShipmentStatusChange = useCallback((status: string[]) => {
    dispatchFilters({
      type: FilterType.SET_SHIPMENT_STATUS,
      payload: status,
    });
  }, []);

  const handleAgronomistChange = useCallback((agronomist: string[]) => {
    dispatchFilters({
      type: FilterType.SET_AGRONOMIST,
      payload: agronomist,
    });
  }, []);

  const handleFarmerGroupChange = useCallback((farmerGroup: string[]) => {
    dispatchFilters({
      type: FilterType.SET_FARMER_GROUP,
      payload: farmerGroup,
    });
  }, []);

  const handleDateRangeChange = useCallback(
    (dateRange: [string, string] | null) => {
      dispatchFilters({
        type: FilterType.SET_DATE_RANGE,
        payload: dateRange,
      });
    },
    [],
  );

  useNotifyError({
    error: packageListError,
    msg: packageListError?.message,
  });

  useEffect(() => {
    setPage(0);
    setPageSize(10);
  }, [filters]);

  return (
    <Flex vertical className="package-container">
      {contextHolder}
      <HeaderCard
        headerLabel={t('packageTrackerDashboard.header')}
        headerInfo={t('packageTrackerDashboard.subHeader')}
        filters={
          <>
            <DateRange
              label={t('packageTrackerDashboard.filters.packageCreationDate')}
              accessibilityLabel="Date Range Filter for filtering out packages with a specific date range"
              onChange={handleDateRangeChange}
            />
          </>
        }
      />

      <PackageSummaryWidgetLayout className="mt-4">
        <PackageCountWidgetLayout>
          <PackageCountChart
            {...packageSummary}
            isLoading={isPackageSummaryLoading}
          />
          <PackageCountWidget
            {...packageSummary}
            isLoading={isPackageSummaryLoading}
          />
        </PackageCountWidgetLayout>
        <SignedInvoicesWidget
          {...packageSummary}
          title={t('packageTrackerDashboard.widgets.signedInvoices')}
          isLoading={isPackageSummaryLoading}
          ctaContent={
            filters.isViewingPackagesWithoutInvoice
              ? t('View all')
              : t('View Missing')
          }
          onCtaClick={handleTogglePackagesWithoutInvoice}
        />
        <StuckPackagesWidget
          title={t('packageTrackerDashboard.widgets.stuckPackages')}
          data={count?.stuckPackages}
          isLoading={isStuckPackageLoading}
          onCtaClick={() => handleToggleStuckPackages(true)}
        />
      </PackageSummaryWidgetLayout>

      <Flex vertical className="package-list" align="center" gap={30}>
        <PackageFilterLayout>
          <SearchBox
            label={t('common.filters.search')}
            placeholder={t('packageTrackerDashboard.filters.soSearch')}
            allowClear
            onSearch={handleSoSearchChange}
            onChange={(e) => !e.target?.value && handleSoSearchChange('')}
            showLabel={true}
          />
          <MultiSelect
            label={t('packageTrackerDashboard.filters.packageStatus')}
            placeholder={t(
              'packageTrackerDashboard.filters.packageStatusPlaceholder',
            )}
            options={STATUS_FILTER_OPTIONS}
            onChange={handlePackageStatusChange}
            value={filters.packageStatus}
            allowClear
            style={{ minWidth: '220px' }}
          />

          <MultiSelect
            label={t('packageTrackerDashboard.filters.shipmentStatus')}
            placeholder={t(
              'packageTrackerDashboard.filters.shipmentStatusPlaceholder',
            )}
            options={SHIPMENT_STATUS_FILTER_OPTIONS}
            onChange={handleShipmentStatusChange}
            value={filters.shipmentStatus}
            allowClear
            style={{ minWidth: '220px' }}
          />

          <ProtectedResource permission="user:read">
            <MultiSelect
              label={t('common.filters.agronomist')}
              placeholder={t('common.filters.agronomistPlaceholder')}
              options={agronomistFilterOptions || []}
              value={filters.agronomist}
              onChange={handleAgronomistChange}
              disabled={isAgronomistLoading}
              allowClear
              style={{ minWidth: '220px' }}
            />
          </ProtectedResource>

          <MultiSelect
            label={t('common.filters.farmerGroup')}
            allowClear
            showSearch
            onSearch={setFarmerGroupSearchTerm}
            placeholder={t('common.filters.farmerGroupPlaceholder')}
            loading={isFarmerGroupLoading}
            options={farmerGroupOptions}
            value={filters.farmerGroup}
            onChange={handleFarmerGroupChange}
          />

          <SwitchComponent
            label={t('packageTrackerDashboard.filters.viewStuck')}
            isLoading={false}
            isChecked={filters.isViewingStuckPackages}
            onChange={handleToggleStuckPackages}
            disabled={false}
          />
        </PackageFilterLayout>

        <PackageTable
          isViewingStuckPackages={filters.isViewingStuckPackages}
          isLoading={isSalesOrderListLoading}
          onPackageRowClick={handlePackageRowSelection}
          data={packageList || []}
          pagination={{
            pageSize,
            current: page + 1,
            total: totalPackageCount,
            onChange: handlePageSizeChange,
            onShowSizeChange: handlePageSizeChange,
          }}
          highlight={selectedPackage?.id}
        />

        <PackageDetailsDrawer
          isOpen={!!selectedPackage}
          pkg={selectedPackage!}
          onClose={() => handlePackageRowSelection(null)}
        />
      </Flex>
    </Flex>
  );
}

export default PackageTracker;
